import _ from 'lodash';
import { Field, useForm } from 'react-final-form';
import { CopyableInputField } from '@/core/CopyableInputField.molecule';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getFormFieldProps } from '@/formbuilder/formbuilder.utilities';
import type { StyleXStyles } from '@stylexjs/stylex';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import type { AppendedProps } from '@seeqdev/qomponents';

export interface CopyableInputProps extends ValidatingFormComponent<string> {
  component: 'CopyableInputFormComponent';
  /** an identifier to be used in tests */
  testId: string;
  /** true if the field should be read only, false otherwise */
  readOnly: boolean;
  /** string to display in the tooltip when hovering the input field */
  fieldTooltip: string;
  /** string to display in the tooltip when hovering the copy button */
  copyButtonTooltip: string;
  /** value to show in the input field */
  value?: string;
  /** notification message sent to user when clicking the copy button */
  copyNotifyMessage?: string;
  /** the extra buttons to render between the input field and the copy button */
  extraButtons?: AppendedProps[];
  copyDisabled?: boolean;
  /** custom styling for the input field */
  extraClassNames?: string;
  xstyle?: StyleXStyles;
  /** custom styling for the copy button */
  buttonExtraClassNames?: string;
  buttonXstyle?: StyleXStyles;
}

export const CopyableInputFormComponent = (props: CopyableInputProps) => {
  const {
    name,
    testId,
    validation,
    readOnly,
    fieldTooltip,
    copyButtonTooltip,
    copyNotifyMessage,
    extraButtons,
    label,
    copyDisabled,
    extraClassNames,
    xstyle,
    buttonExtraClassNames,
    buttonXstyle,
  } = props;

  const formState = useForm().getState();
  const showError =
    _.has(formState.errors, name) &&
    formState.hasValidationErrors &&
    ((formState.modified && formState.modified[name]) || formState.submitFailed);
  const defaultValidation = (v: string | undefined) => _.isEmpty(_.trim(v));
  const appliedValidation = _.isFunction(validation) ? validation : defaultValidation;

  return (
    <FormFieldWrapper testId={testId} label={label} showError={showError}>
      <Field name={name} validate={appliedValidation}>
        {({ input, meta }) => {
          const { onChange, value } = getFormFieldProps(formState, input, meta, props);
          return (
            <CopyableInputField
              testId={`${testId}Field`}
              readOnly={readOnly}
              fieldTooltip={fieldTooltip}
              value={value}
              notifyMessage={copyNotifyMessage}
              buttonTooltip={copyButtonTooltip}
              extraButtons={extraButtons}
              onChange={onChange}
              showError={showError}
              copyDisabled={copyDisabled}
              extraClassNames={extraClassNames}
              xstyle={xstyle}
              buttonExtraClassNames={buttonExtraClassNames}
              buttonXstyle={buttonXstyle}
            />
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};
